import { ReactNode, createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { API_FORBIDDEN_EVENT } from '../../lib/api'
import { getConfig } from '../../lib/config'
import styles from './Toasts.module.scss'

type ToastKind = 'success' | 'error' | 'warning'
type Toast = { id: number; kind: ToastKind; message: string }
export type Flash = { notice?: string; warning?: string }

type ToastActions = {
  showToast: (kind: ToastKind, message: string) => void
  showFlash: (flash: Flash) => void
  showFlashAfterNavigation: (flash: Flash) => void
}

const ToastContext = createContext<ToastActions | null>(null)

let flashAfterNavigation: Flash = {}

/** For a route loader that redirects: it has no hook context, so the flash waits here until the destination renders. */
export function queueFlashAfterNavigation(flash: Flash): void {
  flashAfterNavigation = flash
}

const TOAST_TIMEOUT_MS = 5000
const MAX_TOASTS = 3

/**
 * A toast belongs to the screen it was raised on: leaving that pathname dismisses it. A save that
 * navigates hands its notice to showFlashAfterNavigation, which shows it once the next location
 * renders, so it announces the destination instead of being dismissed with the form.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { key, pathname } = useLocation()
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextIdRef = useRef(1)
  // Mirrors `toasts` so showToast can read the current stack without a stale
  // closure, and without doing the lookup inside a setState updater.
  const toastsRef = useRef<Toast[]>([])
  const timersRef = useRef(new Map<number, number>())
  const shownPathnameRef = useRef(pathname)

  const replaceAll = (next: Toast[]) => {
    toastsRef.current = next
    setToasts(next)
  }

  const clearTimer = (id: number) => {
    window.clearTimeout(timersRef.current.get(id))
    timersRef.current.delete(id)
  }

  const dismiss = (id: number) => {
    clearTimer(id)
    replaceAll(toastsRef.current.filter((toast) => toast.id !== id))
  }

  const scheduleDismiss = (id: number) => {
    window.clearTimeout(timersRef.current.get(id))
    timersRef.current.set(id, window.setTimeout(() => dismiss(id), TOAST_TIMEOUT_MS))
  }

  const showToast = (kind: ToastKind, message: string) => {
    const showing = toastsRef.current.find((toast) => toast.kind === kind && toast.message === message)
    if (showing) {
      scheduleDismiss(showing.id)
      return
    }

    const id = nextIdRef.current
    nextIdRef.current += 1
    const next = [...toastsRef.current, { id, kind, message }]
    next.slice(0, -MAX_TOASTS).forEach((dropped) => clearTimer(dropped.id))
    scheduleDismiss(id)
    replaceAll(next.slice(-MAX_TOASTS))
  }

  const showFlash = ({ notice, warning }: Flash) => {
    if (notice) {
      showToast('success', notice)
    }
    if (warning) {
      showToast('warning', warning)
    }
  }

  const showFlashAfterNavigation = (flash: Flash) => {
    queueFlashAfterNavigation(flash)
  }

  useEffect(() => {
    showFlash(getConfig().flash)
  }, [])

  // Before paint, so the new screen never shows without its flash or with the old screen's toasts.
  useLayoutEffect(() => {
    if (pathname !== shownPathnameRef.current) {
      shownPathnameRef.current = pathname
      toastsRef.current.forEach((toast) => clearTimer(toast.id))
      replaceAll([])
    }
    showFlash(flashAfterNavigation)
    flashAfterNavigation = {}
  }, [key])

  useEffect(() => {
    const handleForbidden = (event: CustomEvent<string>) => showToast('error', event.detail)
    window.addEventListener(API_FORBIDDEN_EVENT, handleForbidden)
    return () => window.removeEventListener(API_FORBIDDEN_EVENT, handleForbidden)
  }, [])

  return (
    <ToastContext.Provider value={{ showToast, showFlash, showFlashAfterNavigation }}>
      {children}
      <div className={styles.container}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            data-testid="toast"
            data-kind={toast.kind}
            className={`${styles.toast} ${styles[toast.kind]}`}
            onClick={() => dismiss(toast.id)}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function useToastActions(): ToastActions {
  const actions = useContext(ToastContext)
  if (!actions) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return actions
}

export function useToast(): ToastActions['showToast'] {
  return useToastActions().showToast
}

export function useFlash(): Pick<ToastActions, 'showFlash' | 'showFlashAfterNavigation'> {
  const { showFlash, showFlashAfterNavigation } = useToastActions()
  return { showFlash, showFlashAfterNavigation }
}
